import { get, IHttpRequestConfig, post, put, remove } from "@/browser";
import { applyHeaders, extractDataFromResponse } from "@/core/api";
import { compose } from "@/core/url";
import { ICodelist } from "@/data/codelists";
import { TUserRole } from "@/data/session";

import {
  cashPaymentDetailsPoland,
  companyAddressArray,
  companyBankingDetailType,
  companyBankingDetailTypeArray,
  companyContactType,
  companyInsuranceType,
  companyPaymentTypeArray,
  companyType,
  companyWithCompletenessType,
  createCompanyValidationType,
  IAddress,
  ICashPaymentDetails,
  ICompany,
  ICompanyContact,
  ICompanyDto,
  ICompanyInsurance,
  ICreateCompanyValidation,
  IPaymentType,
  IRequestToJoinCompany,
  IRequestToJoinCompanyDto,
  ISupplierBankingDetails,
  requestToJoinCompanyType,
} from "./types-ensure";

const API = {
  fetchCompany: "/api/companies/{companyId}",
  addCurrentUserCompany: "/api/users/current/companies",
  put: "/api/companies/{companyId}",
  requestToJoin: "/api/companies/request-to-join",
  accept: `/api/companies/request-to-join/{requestToJoinId}/accept/{role}`,
  reject: `/api/companies/request-to-join/{requestToJoinId}/reject`,
  validateCreate: "/api/companies/validate-create",
  createAddress: (companyId: number) => `/api/companies/company-address/${companyId}/create`,
  updateAddress: () => "/api/companies/company-address",
  getAddress: (companyId: number) => `/api/companies/company-address/${companyId}`,
  getCompanyContact: (companyId: number) => `/api/companies/company-contact/${companyId}`,
  createCompanyContact: (companyId: number) => `/api/companies/company-contact/${companyId}/create`,
  updateCompanyContact: (contactId: number) => `/api/companies/company-contact/${contactId}`,
  getCompanyInsurance: (companyId: number) => `/api/companies/company-insurance/${companyId}`,
  createCompanyInsurance: (companyId: number) => `/api/companies/company-insurance/${companyId}/create`,
  updateCompanyInsurance: (insuranceId: number) => `/api/companies/company-insurance/${insuranceId}`,
  getPaymentType: (countryCode: string) => `/api/companies/payment-type/${countryCode}`,
  getBankingDetails: (companyId: number) => `/api/companies/company-payment-type/${companyId}`,
  makePrimary: (paymentId: number) => `/api/companies/company-payment-type/make-primary/${paymentId}`,
  createBankingDetails: (companyId: number) => `/api/companies/company-payment-type/${companyId}/create`,
  updateBankingDetails: (paymentId: number) => `/api/companies/company-payment-type/${paymentId}`,
  getCashPaymentDetails: (companyId: number) => `/api/companies/cash-payment-details/${companyId}`,
  deleteBankingDetails: (paymentId: number) => `/api/companies/company-payment-type/${paymentId}`,
};

interface IAddCurrentUserCompanyParams {}

export const addCurrentUserCompany = (_params: IAddCurrentUserCompanyParams, data: ICompanyDto) => {
  return post<Partial<ICompany>>(API.addCurrentUserCompany, data).then(extractDataFromResponse).then(companyType);
};

export interface IPutCompanyParams {
  companyId: number;
}

export const putCompany = (params: IPutCompanyParams, data?: ICompany, options?: IHttpRequestConfig) => {
  const url = compose(API.put, params);
  return put<ICompany>(url, data, options).then(extractDataFromResponse).then(companyType);
};

export const createAddress = (id: number, data?: IAddress[], options?: IHttpRequestConfig) => {
  return post<ICompany>(API.createAddress(id), data, options).then(extractDataFromResponse).then(companyType);
};

export const createCompanyContact = (companyId: number, data?: ICompanyContact, options?: IHttpRequestConfig) => {
  return post<ICompany>(API.createCompanyContact(companyId), data, options)
    .then(extractDataFromResponse)
    .then(companyType);
};

export const createCompanyInsurance = (companyId: number, data?: ICompanyInsurance, options?: IHttpRequestConfig) => {
  return post<ICompanyInsurance>(API.createCompanyInsurance(companyId), data, options)
    .then(extractDataFromResponse)
    .then(companyInsuranceType);
};

export const createOrUpdateCashPaymentDetails = (data?: ICashPaymentDetails, options?: IHttpRequestConfig) => {
  return post<ICashPaymentDetails>("/api/companies/cash-payment-details", data, options).then(extractDataFromResponse);
};

export const createBankingDetails = (
  companyId: number,
  data?: ISupplierBankingDetails,
  options?: IHttpRequestConfig,
) => {
  return post<ISupplierBankingDetails>(API.createBankingDetails(companyId), data, options)
    .then(extractDataFromResponse)
    .then(companyBankingDetailType);
};

export const putAddress = (data?: IAddress[], options?: IHttpRequestConfig) => {
  return put<IAddress[]>(API.updateAddress(), data, options).then(extractDataFromResponse).then(companyAddressArray);
};

export const putCompanyContact = (id: number, data?: ICompanyContact, options?: IHttpRequestConfig) => {
  return put<ICompany>(API.updateCompanyContact(id), data, options).then(extractDataFromResponse).then(companyType);
};

export const putCompanyInsurance = (id: number, data?: ICompanyInsurance, options?: IHttpRequestConfig) => {
  return put<ICompanyInsurance>(API.updateCompanyInsurance(id), data, options)
    .then(extractDataFromResponse)
    .then(companyInsuranceType);
};

export const putBankingDetails = (id: number, data?: ISupplierBankingDetails, options?: IHttpRequestConfig) => {
  return put<ISupplierBankingDetails>(API.updateBankingDetails(id), data, options)
    .then(extractDataFromResponse)
    .then(companyBankingDetailType);
};

export const makeBankingDetailPrimary = (id: number, data?: ISupplierBankingDetails, options?: IHttpRequestConfig) => {
  return put<ISupplierBankingDetails>(API.makePrimary(id), data, options)
    .then(extractDataFromResponse)
    .then(companyBankingDetailType);
};

export interface IRequestToJoinCompanyParams {}

export const requestToJoin = (
  params: IRequestToJoinCompanyParams,
  data: IRequestToJoinCompanyDto,
  options?: IHttpRequestConfig,
) => {
  const url = compose(API.requestToJoin, params);
  return post<IRequestToJoinCompany>(url, data, options).then(extractDataFromResponse).then(requestToJoinCompanyType);
};

export interface IActionRequestToJoinParams {
  requestToJoinId: number;
  action: "accept" | "reject";
  role?: TUserRole | null;
}

export const actionRequestToJoin = (params: IActionRequestToJoinParams, options?: IHttpRequestConfig) => {
  const url = compose(params.action === "accept" ? API.accept : API.reject, params);
  return put<IRequestToJoinCompany>(url, {}, options).then(extractDataFromResponse).then(requestToJoinCompanyType);
};

export interface IFetchCompanyParams {
  companyId: number;
}

export const fetchCompany = (params: IFetchCompanyParams, options?: IHttpRequestConfig) => {
  const url = compose(API.fetchCompany, params, {});
  return get<ICompany>(url, applyHeaders(params, options))
    .then(extractDataFromResponse)
    .then(companyWithCompletenessType);
};

export const fetchAddress = (companyId: number, options?: IHttpRequestConfig) => {
  return get<IAddress[]>(API.getAddress(companyId), options).then(extractDataFromResponse).then(companyAddressArray);
};

export const fetchCompanyContact = (companyId: number, options?: IHttpRequestConfig) => {
  return get<ICompanyContact>(API.getCompanyContact(companyId), options)
    .then(extractDataFromResponse)
    .then(companyContactType);
};

export const fetchPaymentTypes = (countryCode: string, options?: IHttpRequestConfig) => {
  return get<IPaymentType[]>(API.getPaymentType(countryCode), options)
    .then(extractDataFromResponse)
    .then(companyPaymentTypeArray);
};

export const fetchBankingDetails = (companyId: number, options?: IHttpRequestConfig) => {
  return get<ISupplierBankingDetails[]>(API.getBankingDetails(companyId), options)
    .then(extractDataFromResponse)
    .then(companyBankingDetailTypeArray);
};

export const fetchCompanyInsurance = (companyId: number, options?: IHttpRequestConfig) => {
  return get<ICompanyInsurance>(API.getCompanyInsurance(companyId), options)
    .then(extractDataFromResponse)
    .then(companyInsuranceType);
};

export const fetchCashPaymentDetails = (companyId: number, options?: IHttpRequestConfig) => {
  return get<ICompanyInsurance>(API.getCashPaymentDetails(companyId), options)
    .then(extractDataFromResponse)
    .then(cashPaymentDetailsPoland);
};

export interface IValidateCreateParams {
  countryCode: ICodelist["code"];
  vatNo: string;
  orgNo: string;
}

export const validateCreateCompany = (params: IValidateCreateParams, options?: IHttpRequestConfig) => {
  const url = compose(API.validateCreate, {});
  return post<ICreateCompanyValidation>(url, params, options)
    .then(extractDataFromResponse)
    .then(createCompanyValidationType);
};

export const deleteBankingDetail = (bankingId: number, options?: IHttpRequestConfig) => {
  return remove<ISupplierBankingDetails>(API.deleteBankingDetails(bankingId), options)
    .then(extractDataFromResponse)
    .then(companyBankingDetailType);
};
